import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  Modal,
  Form,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Label,
  Input,
} from "reactstrap";
import { ToastContainer } from "react-toastify";
import { useFormik } from "formik";
import * as Yup from "yup";
import { createRegion, editRegion, deleteRegion } from "../../api/regions";
import TableContainer from "../../Components/Common/TableContainer";
import DeleteModal from "../../Components/Common/DeleteModal";
import Loader from "../../Components/Common/Loader";
import "react-toastify/dist/ReactToastify.css";
import { getRegionsQuery } from "../../queries";
import { useTranslation } from "react-i18next";

const Regions = () => {
  const [page, setPage] = useState(1);
  const [modal, setModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [region, setRegion] = useState([]);
  const [deleteModal, setDeleteModal] = useState(false);
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const { data: regions } = useQuery({
    ...getRegionsQuery({
      size: "10",
      page,
      order: "desc",
      orderBy: "Id",
    }),
  });

  const toggle = useCallback(() => {
    if (modal) {
      setModal(false);
      setRegion(null);
    } else {
      setModal(true);
    }
  }, [modal]);

  const onDelete = () => {
    deleteRegion(deleteModal)
      .then(() => {
        queryClient.invalidateQueries({ queryKey: ["regions"] });
      })
      .catch((err) => {
        if (err?.data.error?.message) {
          alert(err?.data.error?.message);
        }
      })
      .finally(() => {
        setDeleteModal(false);
      });
  };

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      regionType: (region && region.terminalId) || "Region",
      upperId: (region && region.upperId) || null,
      codes: (region && region.codes) || [
        {
          id: 0,
          type: "cbu",
          code: "",
        },
      ],
      locales: useMemo(() => {
        const locales = region?.locales || [];
        const localeKeys = ["uz", "ru", "uzc"];

        // Fill missing locales with empty values
        return localeKeys.map((localeKey) => {
          const existingLocale = locales.find(
            (locale) => locale.localeKey === localeKey
          );
          return existingLocale || { localeKey, title: "" };
        });
      }, [region]),
    },
    validationSchema: Yup.object({
      regionType: Yup.string()
        .oneOf(
          ["Country", "Region", "City", "Town", "District", "Bazaar"],
          "Invalid region type"
        )
        .required("Region type is required"),
      codes: Yup.array()
        .of(
          Yup.object({
            type: Yup.string().required("Code type is required"),
            code: Yup.string().required("Code is required"),
          })
        )
        .min(1, "At least one code is required"),
      locales: Yup.array()
        .of(
          Yup.object({
            localeKey: Yup.string()
              .oneOf(["uz", "ru", "uzc"], "Invalid locale key")
              .required("Locale key is required"),
            title: Yup.string().required("Title is required"),
          })
        )
        .length(3, "Must have exactly 3 locales"),
    }),
    onSubmit: (values) => {
      if (isEdit) {
        editRegion(region.id, { ...region, ...values })
          .then(() => queryClient.invalidateQueries({ queryKey: ["regions"] }))
          .catch((err) => console.log("Error updating region:", err));
      } else {
        createRegion(values)
          .then(() => queryClient.invalidateQueries({ queryKey: ["regions"] }))
          .catch((err) => console.log("Error creating region:", err));
      }
      toggle();
    },
  });

  const columns = useMemo(
    () => [
      {
        header: t("Name"),
        cell: ({ row }) => {
          const locales = row.original.locales || [];
          const ruTitle = locales.find((l) => l.localeKey === "ru")?.title;
          return ruTitle || locales[0]?.title || "Unknown Region";
        },
      },
      {
        header: t("Region Code"),
        cell: ({ row }) => {
          const codes = row.original.codes || [];
          const code = codes.find((c) => c.type === "cbu")?.code;
          return code || "-";
        },
      },
      {
        header: t("Region Type"),
        accessorKey: "regionType",
        enableColumnFilter: false,
      },
      {
        header: t("Actions"),
        cell: ({ row }) => (
          <>
            <button
              className="btn btn-sm btn-primary"
              onClick={() => handleEdit(row.original)}
            >
              <i className="ri-edit-fill"></i>
            </button>
            <button
              className="btn btn-sm btn-danger"
              style={{ marginLeft: "10px" }}
              onClick={() => setDeleteModal(row.original.id)}
            >
              <i className="ri-delete-bin-6-fill"></i>
            </button>
          </>
        ),
      },
    ],
    [t]
  );

  const handleEdit = (terminal) => {
    setRegion(terminal);
    setIsEdit(true);
    toggle();
  };

  useEffect(() => {
    if (page > 1 && regions) {
      if (regions.data.data.length === 0) {
        setPage(1);
      }
    }
  }, [regions, page]);

  document.title = "Regions | Bazaar";

  return (
    <div className="page-content">
      <DeleteModal
        show={deleteModal}
        onDeleteClick={onDelete}
        onCloseClick={() => setDeleteModal(false)}
      />
      <Container fluid>
        <Row>
          <Col lg={12}>
            <Card id="terminalList">
              <CardHeader className="border-0 ">
                <Row className="align-items-center gy-3">
                  <div className="col-sm">
                    <h5 className="mb-0 fw-bold">{t("Regions")}</h5>
                  </div>
                  <div className="col-sm-auto">
                    <div className="d-flex gap-1 flex-wrap">
                      <button
                        type="button"
                        className="btn btn-success add-btn"
                        id="create-btn"
                        onClick={() => {
                          setIsEdit(false);
                          toggle();
                        }}
                      >
                        <i className="ri-add-line align-bottom me-1"></i>{" "}
                        {t("create")}
                      </button>
                    </div>
                  </div>
                </Row>
              </CardHeader>
              <div className="py-2"></div>
              <div className="card-body pt-0">
                <div>
                  {regions?.data?.data.length ? (
                    <TableContainer
                      columns={columns}
                      data={regions?.data?.data || []}
                      isAddUserList={false}
                      customPageSize={10}
                      pagination={{
                        setPage: setPage,
                        page: page,
                        total: regions?.pagination.TotalPages,
                      }}
                      divClass="table-responsive table-card mb-3"
                      tableClass="align-middle table-nowrap mb-0"
                      theadClass="table-light table-nowrap"
                      thClass="text-muted"
                    />
                  ) : (
                    <Loader />
                  )}
                </div>
                <Modal isOpen={modal} toggle={toggle} centered>
                  <ModalHeader className="bg-light p-3" toggle={toggle}>
                    {!!isEdit ? t("edit") : t("create")}
                  </ModalHeader>
                  <Form
                    className="tablelist-form"
                    onSubmit={(e) => {
                      e.preventDefault();
                      validation.handleSubmit();
                      return false;
                    }}
                  >
                    <ModalBody>
                      <div className="mb-3">
                        <Label htmlFor="regionType" className="form-label">
                          {t("Region Type")}
                        </Label>
                        <Input
                          name="regionType"
                          id="regionType"
                          type="select"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.regionType}
                        >
                          <option value="Country">Country</option>
                          <option value="Region">Region</option>
                          <option value="City">City</option>
                          <option value="Town">Town</option>
                          <option value="District">District</option>
                          <option value="Bazaar">Bazaar</option>
                        </Input>
                        {validation.errors.regionType &&
                          validation.touched.regionType && (
                            <div className="text-danger">
                              {validation.errors.regionType}
                            </div>
                          )}
                      </div>

                      <div className="mb-3">
                        <Label htmlFor="code" className="form-label">
                          {t("Region Code")}
                        </Label>
                        <Input
                          name="codes[0].code"
                          id="code"
                          placeholder={t("Region Code")}
                          type="text"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.codes[0].code}
                        />
                        {validation.errors.codes?.[0]?.code &&
                          validation.touched.codes?.[0]?.code && (
                            <div className="text-danger">
                              {validation.errors.codes[0].code}
                            </div>
                          )}
                      </div>

                      {["uz", "ru", "uzc"].map((key) => {
                        const localeIndex = validation.values.locales.findIndex(
                          (locale) => locale.localeKey === key
                        );
                        return localeIndex !== -1 ? (
                          <div className="mb-3" key={key}>
                            <Label className="form-label">
                              {t("Name")} ({key.toUpperCase()})
                            </Label>
                            <Input
                              name={`locales[${localeIndex}].title`}
                              placeholder={`${t("Name")} (${key})`}
                              type="text"
                              onChange={validation.handleChange}
                              onBlur={validation.handleBlur}
                              value={
                                validation.values.locales[localeIndex].title
                              }
                            />
                            {validation.errors.locales?.[localeIndex]?.title &&
                              validation.touched.locales?.[localeIndex]
                                ?.title && (
                                <div className="text-danger">
                                  {validation.errors.locales[localeIndex].title}
                                </div>
                              )}
                          </div>
                        ) : null;
                      })}
                    </ModalBody>
                    <ModalFooter>
                      <button
                        type="button"
                        className="btn btn-light"
                        onClick={toggle}
                      >
                        {t("Close")}
                      </button>
                      <button type="submit" className="btn btn-success">
                        {!!isEdit ? t("Update") : t("create")}
                      </button>
                    </ModalFooter>
                  </Form>
                </Modal>
                <ToastContainer closeButton={false} limit={1} />
              </div>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default Regions;
